'use client';

/**
 * ProductSearchInputCard — the search INPUT control. ONE concept.
 *
 * Surface-agnostic and relocatable: renders its own neutral container
 * (NOT RailCard), omits `surface` in useCardExport (the placement
 * provider supplies it), so the matrix can mount it on the rail, the
 * explorer, or a top bar by placement alone. Writes {query} to the
 * product-search store and flips the mode4 selection to `search`; the
 * results paint on the canvas. Facets are a SEPARATE card
 * (product_facets) so the two relocate independently.
 *
 * @see feedback_cards-surface-agnostic-relocatable
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="product_search"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useProductSearchStore } from '@/lib/stores/product-search-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useProductLabel } from '@/lib/stores/total-matrix-store';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';

export function ProductSearchInputCard() {
  const query = useProductSearchStore((s) => s.query);
  const setSearch = useProductSearchStore((s) => s.setSearch);
  const reset = useProductSearchStore((s) => s.reset);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const [draft, setDraft] = useState(query);

  const labelTitle = useProductLabel('search_title');
  const labelPlaceholder = useProductLabel('search_placeholder');
  const labelGo = useProductLabel('search_go');
  const labelClear = useProductLabel('search_clear');

  const active = query.trim().length > 0;

  const submit = () => {
    const q = draft.trim();
    if (!q) return;
    setSearch(q); // fresh query clears filters
    setExplorerSelection('search'); // swap the canvas to the results grid
  };
  const clear = () => {
    setDraft('');
    reset();
    setExplorerSelection(null); // back to the vendor wall / browse
  };

  // surface/slot_id come from the CardPlacementProvider — omitted so the
  // card labels correctly wherever the matrix mounts it.
  useCardExport(
    useMemo(
      () => ({
        label: 'Search',
        content_key: 'product_search',
        status: 'loaded' as const,
        query_key: null,
        text: active ? `search: ${query}` : 'Search',
        json: { query },
      }),
      [active, query],
    ),
  );

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <Search className="h-3.5 w-3.5 text-muted-foreground" />
        <span className={text.heading}>{labelTitle}</span>
      </div>
      <div className="p-3">
        <div className="flex gap-1">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder={labelPlaceholder}
            className="min-w-0 flex-1 rounded border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
          />
          <button
            onClick={submit}
            className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground hover:opacity-90"
          >
            {labelGo}
          </button>
          {active && (
            <button
              onClick={clear}
              className="rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
              title={labelClear}
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
